import React, { useMemo } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { normalizeType, normalizeKey } from '../utils/normalize';
import AppSelect from './AppSelect';

export default function CreateIrishSetForm({ user, inputStyle, tunes, showForm }) {
  const [irishType, setIrishType] = React.useState('');
  const [keys, setKeys] = React.useState(['', '', '']);
  const navigate = useNavigate();
  const [keyMode, setKeyMode] = React.useState('manual'); // 'manual' or 'surprise'

  const allIrishTunes = useMemo(() => {
    return (tunes || []).filter(t => t.genre === 'Irish');
  }, [tunes]);

  const allTypes = useMemo(() => {
    const typeSet = new Set();
    allIrishTunes.forEach(t => {
      const norm = normalizeType(t.type);
      if (norm) typeSet.add(norm);
    });
    return Array.from(typeSet).sort();
  }, [allIrishTunes]);

  const allMatchingTunes = useMemo(() => {
    return allIrishTunes.filter(t =>
      normalizeType(t.type) === normalizeType(irishType)
    );
  }, [allIrishTunes, irishType]);

  const keyCounts = useMemo(() => {
    const counts = {};
    allMatchingTunes.forEach(t => {
      const norm = normalizeKey(t.key);
      if (norm) counts[norm] = (counts[norm] || 0) + 1;
    });
    return counts;
  }, [allMatchingTunes]);

  const availableKeysWithCounts = useMemo(() => {
    const usedCounts = keys.reduce((acc, key) => {
      const norm = normalizeKey(key);
      if (norm) acc[norm] = (acc[norm] || 0) + 1;
      return acc;
    }, {});

    const result = {};
    Object.entries(keyCounts).forEach(([key, total]) => {
      const used = usedCounts[key] || 0;
      result[key] = total - used;
    });
    return result;
  }, [keys, keyCounts]);
  const irishTypeOptions = useMemo(
    () => [{ value: '', label: 'Type' }, ...allTypes.map((t) => ({ value: t, label: t }))],
    [allTypes]
  );
  const keyModeOptions = useMemo(
    () => [
      { value: 'manual', label: 'Select keys' },
      { value: 'surprise', label: 'Surprise me' }
    ],
    []
  );

  const handleKeyChange = (index, value) => {
    const newKeys = [...keys];
    newKeys[index] = value;
    setKeys(newKeys);
  };

  const handleRemoveKey = (index) => {
    if (keys.length <= 2) return;
    const newKeys = keys.filter((_, i) => i !== index);
    setKeys(newKeys);
  };

const handleCreateSet = async () => {
  const selected = [];
  const usedTuneIds = new Set();

  let matchingPool = allMatchingTunes;

  if (keyMode === 'manual') {
    const validKeys = keys.map(normalizeKey).filter(Boolean);
    for (const key of validKeys) {
      const matches = matchingPool.filter(t =>
        normalizeKey(t.key) === key && !usedTuneIds.has(t.id)
      );
      if (matches.length > 0) {
        const randomIndex = Math.floor(Math.random() * matches.length);
        const chosen = matches[randomIndex];
        selected.push(chosen);
        usedTuneIds.add(chosen.id);
      }
    }
  } else {
    // Surprise mode: pick N random tunes from full pool of correct type, no repeats
    const available = matchingPool.filter(t => !usedTuneIds.has(t.id));
    const shuffled = [...available].sort(() => 0.5 - Math.random());

    for (let i = 0; i < keys.length && i < shuffled.length; i++) {
      selected.push(shuffled[i]);
      usedTuneIds.add(shuffled[i].id);
    }
  }

  if (selected.length < keys.length) {
    alert("Not enough unique tunes found to create a complete set.");
    return;
  }

const titlePrefix =
  keyMode === 'surprise'
    ? `${irishType} Set (Surprise)`
    : `${irishType} Set (Generated)`;
const title = `${titlePrefix}: ${selected.map(t => normalizeKey(t.key)).join(' / ')}`;
  const newSet = {
    title,
    notes: '',
    tunes: selected.map(t => ({ id: t.id, title: t.title })),
    mode: 'tunes',
    createdAt: new Date().toISOString()
  };

  const setId = crypto.randomUUID();
  await setDoc(doc(db, 'users', user.uid, 'sets', setId), { ...newSet, id: setId });
  navigate(`/sets/${setId}`);
};



  const addKeyField = () => {
    if (keys.length >= 5) return;
    setKeys([...keys, '']);
  };

  if (!showForm) return null;

  return (
    <div style={{ marginTop: '0.75rem', textAlign: 'center', maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '0.5rem',
          width: '100%'
        }}
      >
        <div style={{ minWidth: 0 }}>
          <AppSelect
            className="search-core-select"
            value={irishType}
            onChange={(value) => {
              setIrishType(value);
              setKeys(['', '', '']);
            }}
            options={irishTypeOptions}
            placeholder="Type"
            active={Boolean(irishType)}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <AppSelect
            className="search-core-select"
            value={keyMode}
            onChange={setKeyMode}
            options={keyModeOptions}
            placeholder="Key Mode"
            active={keyMode === 'surprise'}
          />
        </div>
        <button
          className="btn-subtle search-control-btn search-random-btn"
          style={{ minHeight: 'var(--ui-select-height, 28px)', minWidth: 0, width: '100%' }}
          onClick={handleCreateSet}
        >
          + Create
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        {keys.map((val, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: '130px' }}>
            {keyMode === 'surprise' ? (
              <div
                style={{
                  width: '100%',
                  minHeight: 'var(--ui-select-height, 28px)',
                  borderRadius: '12px',
                  border: '1px solid color-mix(in srgb, var(--card-border) 75%, transparent)',
                  background: 'color-mix(in srgb, var(--input-bg) 88%, #0b3d66 12%)',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 var(--ui-select-pad-x, 10px)',
                  fontSize: 'var(--ui-select-font, var(--ui-compact-font))'
                }}
              >
                Key {idx + 1}
              </div>
            ) : (
              <AppSelect
                className="search-advanced-select"
                value={val}
                onChange={(value) => handleKeyChange(idx, value)}
                options={[
                  { value: '', label: `Key ${idx + 1}` },
                  ...Object.entries(keyCounts)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .filter(([k]) => (availableKeysWithCounts[k] ?? 0) > 0 || k === val)
                    .map(([k]) => {
                      const remaining = availableKeysWithCounts[k] ?? 0;
                      return { value: k, label: remaining <= 0 ? k : `${k} (${remaining})` };
                    })
                ]}
                placeholder={`Key ${idx + 1}`}
                active={Boolean(val)}
              />
            )}
            {keys.length > 2 && (
              <button
                type="button"
                onClick={() => handleRemoveKey(idx)}
                className="btn-subtle search-control-btn"
                style={{ minHeight: 'var(--ui-select-height, 28px)', padding: '0 8px' }}
              >
                ✖
              </button>
            )}
          </div>
        ))}

        {keys.length < 5 && (
          <button
            type="button"
            className="btn-subtle search-control-btn search-add-key-btn"
            style={{ minHeight: 'var(--ui-select-height, 28px)', padding: '0 10px', fontSize: '1.1rem', fontWeight: 700 }}
            onClick={addKeyField}
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}
